import { useEffect } from "react";
import { createRouter, useRouter } from "@tanstack/react-router";
import { routeTree } from "./routeTree.gen";

function DefaultErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  const router = useRouter();

  // Synchronous log at render time. The useEffect below still runs once the
  // boundary mounts, but effects can be skipped/deferred in some cases (e.g.
  // when the error occurs during streaming SSR hydration). Logging here
  // guarantees the real error surfaces in the browser console on the first
  // paint of the fallback UI.
  {
    const componentStack =
      (error as unknown as { componentStack?: string }).componentStack ?? null;
    // eslint-disable-next-line no-console
    console.error(
      "[DefaultErrorComponent] render-time error",
      "\nmessage:", error?.message,
      "\nstack:", error?.stack,
      "\ncomponentStack:", componentStack,
      "\nerror:", error,
    );
  }

  // Surface the real error to the browser console so the next occurrence is
  // diagnosable. The UI itself is unchanged.
  useEffect(() => {
    const componentStack =
      (error as unknown as { componentStack?: string }).componentStack ?? null;
    // eslint-disable-next-line no-console
    console.error(
      "[DefaultErrorComponent] caught render error",
      "\nmessage:", error?.message,
      "\nstack:", error?.stack,
      "\ncomponentStack:", componentStack,
      "\nerror:", error,
    );
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-8 w-8 text-destructive"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"
            />
          </svg>
        </div>
        <h1 className="text-2xl font-bold tracking-tight text-foreground">Something went wrong</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          An unexpected error occurred. Please try again.
        </p>
        {import.meta.env.DEV && error.message && (
          <pre className="mt-4 max-h-40 overflow-auto rounded-md bg-muted p-3 text-left font-mono text-xs text-destructive">
            {error.message}
          </pre>
        )}
        <div className="mt-6 flex items-center justify-center gap-3">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const getRouter = () => {
  const router = createRouter({
    routeTree,
    context: {},
    scrollRestoration: true,
    defaultPreloadStaleTime: 0,
    defaultErrorComponent: DefaultErrorComponent,
  });

  return router;
};
